import React, { useState } from 'react';
import classnames from 'classnames';
import { Box, BoxContent, Heading } from '@mm/components';
import {
  companyFeatureFlagsSelector,
  nextBillingTypeForFeatureSelector,
} from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router';
import { PlanBadge } from '../../../../../common/components/ProBadge';
import { ReactComponent as HelpdocIcon } from '../../../../../layout/BotBuilderSidebar/SidebarHeader/AllFeaturesDropdown/icons/helpdocs.svg';
import { INTEGRATIONS_SECTIONS, PageSettingDefinition } from './constants';
import './Integrations.scss';
import { landmineNames } from '@mm/constants/landmine-names';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { SubscriptionFeatureFlag } from '@mm/state/controllers/FeatureFlags';

export const Integrations = () => {
  const chatbot = useSelector(activeChatbotSelector);

  const location = useLocation();
  const { pathname } = location;
  const history = useHistory();
  const { openUniversalUpgradeLandmineModal } = useModalActions();
  const [helpLink, setHelpLink] = useState(
    'https://mobilemonkey.com/help/l/en/category/06p3o5upol-integrations',
  );
  const isAutomationsEnabled = useSelector(
    companyFeatureFlagsSelector,
  ).automations;

  const getIsActiveMenuItem = (
    pathname: string,
    section: PageSettingDefinition,
  ) => {
    return pathname.includes(section.path(chatbot.id));
  };

  const billingTypes = useSelector((state: StoreState) => [
    nextBillingTypeForFeatureSelector(state, 'nativeIntegrations'),
    nextBillingTypeForFeatureSelector(state, 'zapier'),
    nextBillingTypeForFeatureSelector(state, 'jsonWebhook'),
    nextBillingTypeForFeatureSelector(state, 'mobileMonkeyApi'),
  ]);

  const getUpgradeModalProps = (section: PageSettingDefinition) => {
    switch (section.id) {
      case 'zapier':
        return {
          feature: section.requiredFeature,
          landmineName: landmineNames.INTEGRATIONS_ZAPIER,
        };
      case 'json-webhooks':
        return {
          feature: section.requiredFeature,
          landmineName: landmineNames.INTEGRATIONS_JSON_WEBHOOKS,
        };
      case 'email':
        return {
          feature: 'nativeIntegrations' as SubscriptionFeatureFlag,
          landmineName: landmineNames.INTEGRATIONS_EMAIL,
        };
      default:
        return {
          feature: section.requiredFeature,
          landmineName: landmineNames.INTEGRATIONS_NATIVE_INTEGRATIONS,
        };
    }
  };

  return (
    <section className="integrations">
      <Box className="integrations-c-box">
        <BoxContent className="integrations__content">
          <div className="integrations__content-header">
            <div className="integrations__title">
              <Heading text="Integrations Settings" size={3} />
            </div>
            <div
              className="integrations__help"
              onClick={() => window.open(helpLink, '_blank')}
            >
              <HelpdocIcon className="integrations__help-icon" />
              Help center
            </div>
          </div>
          <div className="integrations__content-topbar">
            {INTEGRATIONS_SECTIONS.map((section, index) => {
              const isActive = getIsActiveMenuItem(pathname, section);
              const billingType = billingTypes[index];
              return (
                <div
                  key={section.id}
                  className={classnames('integrations__content-topbar-link', {
                    'integrations__content-topbar-link-active': isActive,
                    'integrations__content-topbar-link-hidden':
                      section.id === 'notifications' && !isAutomationsEnabled,
                  })}
                  onClick={() => {
                    if (section.helpDoc) {
                      setHelpLink(section.helpDoc);
                    }
                    if (billingType && section.id !== 'native_integrations') {
                      openUniversalUpgradeLandmineModal({
                        feature: getUpgradeModalProps(section).feature,
                        landmineName:
                          getUpgradeModalProps(section).landmineName,
                      });
                    } else {
                      history.push(section.path(chatbot.id));
                    }
                  }}
                >
                  <div className="integrations__content-topbar-link-container">
                    {section.name}
                    {!!billingType && (
                      <PlanBadge
                        className="integrations__content-topbar-link-icon"
                        billingType={billingType}
                        size={10}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <Switch>
            {INTEGRATIONS_SECTIONS.map(section => (
              <Route
                key={section.id}
                exact
                path={section.path(chatbot.id)}
                component={section.component}
              />
            ))}

            <Redirect to={INTEGRATIONS_SECTIONS[0].path(chatbot.id)} />
          </Switch>
        </BoxContent>
      </Box>
    </section>
  );
};
